import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import Paper from '@mui/material/Paper';

const INTERACTIVE_SELECTOR = 'button, a, input, textarea, select, option, [role="button"], [role="combobox"], [role="listbox"], [contenteditable="true"]';
const TITLE_SELECTOR = '.MuiDialogTitle-root, [data-dialog-drag-handle="true"]';

const DraggableDialogPaper = forwardRef(function DraggableDialogPaper({ className = '', ...paperProps }, ref) {
  const paperRef = useRef(null);
  const positionRef = useRef({ x: 0, y: 0 });
  const dragRef = useRef(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const isFullScreen = className.split(' ').includes('MuiDialog-paperFullScreen');

  const setPaperRef = useCallback((node) => {
    paperRef.current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref) ref.current = node;
  }, [ref]);

  const stopDragging = useCallback((event) => {
    const drag = dragRef.current;
    if (!drag || (event?.pointerId != null && event.pointerId !== drag.pointerId)) return;
    dragRef.current = null;
    const paper = drag.paper;
    if (paper) {
      paper.style.cursor = '';
      if (paper.hasPointerCapture?.(drag.pointerId)) paper.releasePointerCapture(drag.pointerId);
    }
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) stopDragging();
    };
    window.addEventListener('pointerup', stopDragging, true);
    window.addEventListener('pointercancel', stopDragging, true);
    window.addEventListener('blur', stopDragging);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('pointerup', stopDragging, true);
      window.removeEventListener('pointercancel', stopDragging, true);
      window.removeEventListener('blur', stopDragging);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      stopDragging();
    };
  }, [stopDragging]);

  const handlePointerDown = (event) => {
    const target = event.target;
    // Nested dialogs/menu portals bubble through React parents, even though
    // they belong to a different paper. Only the dialog under the pointer drags.
    if (!event.currentTarget.contains(target)) return;
    const isInteractiveElement = target.closest?.(INTERACTIVE_SELECTOR);
    const isTitle = target.closest?.(TITLE_SELECTOR);
    if (isFullScreen || dragRef.current || event.button !== 0 || isInteractiveElement || !isTitle) return;

    const currentPosition = positionRef.current;
    dragRef.current = {
      paper: event.currentTarget,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: currentPosition.x,
      originY: currentPosition.y,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    event.currentTarget.style.cursor = 'grabbing';
  };

  const handlePointerMove = (event) => {
    if (!dragRef.current || !paperRef.current || event.pointerId !== dragRef.current.pointerId) return;
    if (event.buttons === 0) {
      stopDragging(event);
      return;
    }
    const bounds = paperRef.current.getBoundingClientRect();
    const maxX = Math.max((window.innerWidth - bounds.width) / 2 - 16, 0);
    const maxY = Math.max((window.innerHeight - bounds.height) / 2 - 16, 0);
    const nextPosition = {
      x: Math.min(Math.max(dragRef.current.originX + event.clientX - dragRef.current.startX, -maxX), maxX),
      y: Math.min(Math.max(dragRef.current.originY + event.clientY - dragRef.current.startY, -maxY), maxY),
    };
    positionRef.current = nextPosition;
    setPosition(nextPosition);
  };

  return (
    <Paper
      {...paperProps}
      className={className}
      ref={setPaperRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={stopDragging}
      onPointerCancel={stopDragging}
      onLostPointerCapture={stopDragging}
      style={{
        ...paperProps.style,
        left: position.x || undefined,
        top: position.y || undefined,
        position: position.x || position.y ? 'relative' : paperProps.style?.position,
      }}
    />
  );
});

export default DraggableDialogPaper;
